// Libs
import React, { FC, useState } from 'react'
import { useHistory, Link } from 'react-router-dom'

// Service
import FirestoreService from '../../../../services/firestoreService'
import { useChatClient } from '../../../messages/components/withStreamClient'
import { useEventActivities, EventActivity } from '../../../../hooks/useEventActivities'
import { useManageClinicNOtherAdminTabNew } from './hooks/useManageClinicNOtherAdminTabNew'
import useToasterHelper from '../../../../helpers/ToasterHelper'

// Helpers
import { getUserFullName } from '../../../../helpers/helpers'
import { getConvertedData } from '../../../../models/interface.helper'

// Models
import { UserModel } from '../../../../models/users/user.model'

// Types
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../../types/payment'

// Constants
import { CONST } from '../../../../const/const'
import { MESSAGES_CONST } from '../../../../const/messages-const'
import { IMAGE_CONSTS } from '../../../../const/image-const'

// Components
import InfiniteScrollDataTable from '../../../../components/common/tables/InfiniteScrollDataTable'
import { TableBtn } from './components/TableBtn'
import ViewsLoader from '../../../../components/loader/ViewsLoader'

interface ManageClinicActivityProps {
  eventId: string
}

export const ManageClinicActivityTab: FC<ManageClinicActivityProps> = ({ eventId }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null)
  const { activities, loading, canLoadMore, loadMore } = useEventActivities(eventId, 20, searchTerm)
  const { createChatEvent } = useChatClient()
  const history = useHistory()
  const toastFunction = useToasterHelper()
  const { filteredRiders } = useManageClinicNOtherAdminTabNew(eventId)

  const tableColumns = [
    {
      name: 'Time',
      cell: (row: EventActivity) => (
        <div className="justify-center w-full text-center text-sm text-[#122B46]/70 sm:text-left whitespace-normal">
          <span className="font-medium text-[#122B46]">{row.time}</span>
          <br />
          {row.date}
        </div>
      ),
      width: '25%',
      style: 'padding:0',
    },
    {
      name: 'Event',
      cell: (row: EventActivity) => (
        <div className="sm:py-4 w-full text-center sm:text-left flex flex-col sm:flex-row justify-center sm:justify-start items-center gap-3">
          <img
            src={row.userAvatar ? row.userAvatar : IMAGE_CONSTS.PLACEHOLDERS.USER}
            onError={(e) => ((e.target as any).src = IMAGE_CONSTS.PLACEHOLDERS.USER)}
            className="w-11 h-11 rounded-full object-cover"
            alt="sender"
          />
          <div className="mt-2 sm:mt-0">
            <div className="font-medium text-[#122B46] break-words">{getUserFullName(row)}</div>
            <div className="text-sm text-[#122B46]/80 break-words">{row.action}</div>
          </div>
        </div>
      ),
      width: 'calc(75% - 140px)',
    },
    {
      name: 'Manage',
      cell: (row: EventActivity) => {
        const rider = filteredRiders.find((rider) => rider.userId === row.userId)
        return (
          <div className="w-full text-center flex flex-wrap gap-2 justify-center sm:justify-end">
            <TableBtn
              type={'message'}
              loading={loadingUserId === row.userId}
              onClick={async () => {
                try {
                  setLoadingUserId(row.userId)

                  const userSnap = await FirestoreService.getItem(
                    CONST.DATA.FIRESTORE.V01.COLLECTIONS.USERS.NAME,
                    row.userId
                  )

                  if (!userSnap.exists()) return

                  const userData = getConvertedData(UserModel.fromFirestoreDoc(userSnap).toObject())

                  const user = {
                    ...userData,
                    name: getUserFullName(userData),
                    role: 'Rider',
                    vertical: 'exhibitors',
                  }

                  await createChatEvent([user], eventId, { eventId: eventId }).then(() => {
                    history.push(`${CONST.ROUTES.MANAGE.CLINIC_N_OTHER.URL}/${eventId}/messages`)
                  })
                } catch (error) {
                  console.error('Error creating chat:', error)
                  toastFunction.error({ message: 'Failed to create chat' })
                } finally {
                  setLoadingUserId(null)
                }
              }}
            />
            {rider && (
              <Link
                to={`${CONST.ROUTES.MANAGE.MANAGE_RIDER.URL}/${row?.userId}/${eventId}/${rider.registrationDocId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (
                    (rider.paymentType === PAYMENT_TYPE.DEFAULT &&
                      rider.paymentStatus === PAYMENT_TYPE.DEFAULT) ||
                    rider.paymentStatus === PAYMENT_STATUS.PENDING
                  ) {
                    e.preventDefault()
                    toastFunction.info({ message: MESSAGES_CONST.NOT_REGISTERED_YET })
                  }
                }}
                className="hover:opacity-70 transition-all"
              >
                <TableBtn type={'go'} />
              </Link>
            )}
          </div>
        )
      },
      width: '140px',
      style: 'padding:0',
      right: true,
    },
  ]

  return (
    <div className="p-3 sm:p-5 rounded-xl bg-SeabiscuitWhiteThemeColor w-full flex flex-col">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <div className="w-full md:w-[48%]">
          <h2 className="text-SeabiscuitMainThemeColor font-bold text-[25px] sm:text-[22px] lg:text-[25px]">
            Event Activity
          </h2>
          <p className="text-SeabiscuitDark200ThemeColor text-sm sm:text-base">
            Search members by name to view their activity
          </p>
        </div>
        <div className="relative my-auto max-w-[400px] w-full md:w-[48%]">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-SeabiscuitGray500ThemeColor w-full"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
      </div>
      {loading && activities.length === 0 ? (
        <div className="w-full min-h-[400px] flex justify-center items-center">
          <ViewsLoader size="lg" color="#F70763" />
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <InfiniteScrollDataTable
            minWidth="400px"
            mobileFrom={480}
            hasMore={canLoadMore}
            fetchMore={loadMore}
            columns={tableColumns}
            data={activities}
            noDataComponent={
              <div className="min-h-[65vh] flex items-center justify-center flex-col gap-[36px]">
                <div>No activities found</div>
              </div>
            }
          />
        </div>
      )}
    </div>
  )
}

// Export without the Knock provider
export default ManageClinicActivityTab
